import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TrendingDown, AlertOctagon, Boxes, Truck, Calendar, Printer, Download } from 'lucide-react';
import { toast } from 'sonner';
import { openBrandedPrintWindow, PrintHelpers as H } from '../utils/brandedPrint';
import { csvCell } from '../lib/utils';

const InventoryVarianceReport = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async (d = days) => {
    setLoading(true);
    try {
      const r = await api().get(`/reports/inventory-variance?days=${d}`);
      setData(r.data);
    } catch {
      toast.error('Failed to load variance report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(days); /* eslint-disable-next-line */ }, []);

  // iter67 — branded printable variance report
  const printReport = async () => {
    if (!data) return;
    const cols = [
      { key: 'item_sku', label: 'SKU', mono: true },
      { key: 'item_name', label: 'Item' },
      { key: 'adjust_count', label: 'Adjustments', mono: true },
      { key: 'total_qty', label: 'Net Qty', mono: true, fmt: (v) => Number(v || 0).toFixed(2) },
      { key: 'total_value', label: 'Value Impact', mono: true, fmt: (v) => `$${Number(v || 0).toFixed(2)}` },
      { key: 'last_event', label: 'Last', fmt: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    ];
    const rows = data.top_skus || data.skus || data.rows || [];
    await openBrandedPrintWindow({
      title: 'INVENTORY VARIANCE REPORT',
      subtitle: `${days}-day window · ${rows.length} SKUs`,
      orientation: 'landscape',
      body: `${H.callout(`<strong>Total adjustments:</strong> ${data.total_adjustments || 0} &nbsp;·&nbsp; <strong>Net qty change:</strong> ${data.net_qty_change ?? 0} &nbsp;·&nbsp; <strong>Value impact:</strong> $${Number(data.total_value_impact || 0).toFixed(2)}`)}${H.sectionTitle('Top SKUs by Adjustment Volume')}${H.table(cols, rows)}`,
      footerNote: 'Internal Document · Variance Report',
    });
  };

  const downloadCsv = () => {
    if (!data) return;
    const rows = data.top_skus || data.skus || data.rows || [];
    const headers = ['SKU', 'Item', 'Adjustments', 'Net Qty', 'Value Impact', 'Last Event'];
    const out = [headers, ...rows.map(r => [
      r.item_sku, r.item_name, r.adjust_count, r.total_qty,
      Number(r.total_value || 0).toFixed(2), r.last_event ? new Date(r.last_event).toISOString() : '',
    ])];
    const csv = out.map(row => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `variance-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${rows.length} rows`);
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="inventory-variance-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-orange-600" /> Inventory Variance Report
          </h1>
          <p className="text-slate-500 text-sm">Adjustment transactions over the selected window — surface chronic shrink offenders.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Window (days)</label>
            <Input
              type="number"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 1)}
              className="rounded-sm mono w-32"
              data-testid="variance-window-input"
            />
          </div>
          <Button onClick={() => fetchReport(days)} className="rounded-sm bg-orange-600 hover:bg-orange-700" data-testid="variance-refresh-btn">
            Refresh
          </Button>
          <Button variant="outline" onClick={() => printReport()} disabled={!data} className="rounded-sm" data-testid="variance-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button variant="outline" onClick={() => downloadCsv()} disabled={!data} className="rounded-sm" data-testid="variance-csv-btn">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
        </div>
      </div>

      {loading && <div className="flex items-center justify-center h-48"><div className="spinner" /></div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="variance-totals">
            <div className="rounded-sm border border-slate-200 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Adjustments</div>
              <div className="text-2xl font-bold text-slate-900 mono">{data.totals.adjustments}</div>
            </div>
            <div className="rounded-sm border border-slate-200 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Items Affected</div>
              <div className="text-2xl font-bold text-slate-900 mono">{data.totals.items_affected}</div>
            </div>
            <div className="rounded-sm border border-slate-200 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Net Δ</div>
              <div className={`text-2xl font-bold mono ${data.totals.net_delta < 0 ? 'text-red-600' : data.totals.net_delta > 0 ? 'text-emerald-700' : 'text-slate-900'}`}>
                {data.totals.net_delta > 0 ? '+' : ''}{data.totals.net_delta}
              </div>
            </div>
            <div className="rounded-sm border border-slate-200 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Absolute Movement</div>
              <div className="text-2xl font-bold text-slate-900 mono">{data.totals.abs_delta}</div>
            </div>
          </div>

          <Tabs defaultValue="items">
            <TabsList>
              <TabsTrigger value="items" data-testid="variance-tab-items">By Item</TabsTrigger>
              <TabsTrigger value="vendors" data-testid="variance-tab-vendors">By Vendor</TabsTrigger>
              <TabsTrigger value="chronic" data-testid="variance-tab-chronic">Chronic Shrink ({data.chronic_shrink.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="items" className="mt-4">
              <Card className="industrial-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Boxes className="w-4 h-4" /> Variance by Item
                  </h2>
                </CardHeader>
                <CardContent className="p-0">
                  {data.by_item.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400">No adjustments in this window.</div>
                  ) : (
                    <Table className="data-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>SKU</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Events</TableHead>
                          <TableHead className="text-right">Negative</TableHead>
                          <TableHead className="text-right">Net Δ</TableHead>
                          <TableHead className="text-right">|Δ|</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.by_item.map((r) => (
                          <TableRow key={r.item_sku} data-testid={`variance-item-row-${r.item_sku}`} className={r.net_delta < 0 ? 'bg-red-50/40' : ''}>
                            <TableCell className="mono">
                              {r.item_id ? (
                                <button onClick={() => navigate(`/items/view/${r.item_id}`)} className="hover:text-orange-600 hover:underline">{r.item_sku}</button>
                              ) : r.item_sku}
                            </TableCell>
                            <TableCell>{r.item_name || '—'}</TableCell>
                            <TableCell className="text-right mono">{r.events}</TableCell>
                            <TableCell className="text-right mono">{r.negative_events}</TableCell>
                            <TableCell className={`text-right mono font-semibold ${r.net_delta < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                              {r.net_delta > 0 ? '+' : ''}{r.net_delta}
                            </TableCell>
                            <TableCell className="text-right mono">{r.abs_delta}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="vendors" className="mt-4">
              <Card className="industrial-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Variance by Vendor
                  </h2>
                </CardHeader>
                <CardContent className="p-0">
                  {data.by_vendor.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400">No adjustments tied to a vendor in this window.</div>
                  ) : (
                    <Table className="data-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vendor</TableHead>
                          <TableHead className="text-right">Events</TableHead>
                          <TableHead className="text-right">SKUs Affected</TableHead>
                          <TableHead className="text-right">Net Δ</TableHead>
                          <TableHead className="text-right">|Δ|</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.by_vendor.map((r) => (
                          <TableRow key={r.vendor_id} data-testid={`variance-vendor-row-${r.vendor_id}`}>
                            <TableCell className="font-medium text-slate-800">{r.vendor_name || '—'}</TableCell>
                            <TableCell className="text-right mono">{r.events}</TableCell>
                            <TableCell className="text-right mono">{r.skus_affected}</TableCell>
                            <TableCell className={`text-right mono font-semibold ${r.net_delta < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                              {r.net_delta > 0 ? '+' : ''}{r.net_delta}
                            </TableCell>
                            <TableCell className="text-right mono">{r.abs_delta}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="chronic" className="mt-4">
              <Card className="industrial-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <AlertOctagon className="w-4 h-4 text-red-600" /> Chronic Shrink Offenders
                  </h2>
                </CardHeader>
                <CardContent className="p-0">
                  {data.chronic_shrink.length === 0 ? (
                    <div className="p-6 text-center text-sm text-emerald-700" data-testid="variance-chronic-empty">No chronic shrink in this window — inventory looks healthy.</div>
                  ) : (
                    <Table className="data-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>SKU</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Negative Events</TableHead>
                          <TableHead className="text-right">Net Δ</TableHead>
                          <TableHead className="text-right">Recommended Reorder Bump</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.chronic_shrink.map((r) => (
                          <TableRow key={r.item_sku} data-testid={`variance-chronic-row-${r.item_sku}`} className="bg-red-50/40">
                            <TableCell className="mono">
                              {r.item_id ? (
                                <button onClick={() => navigate(`/items/view/${r.item_id}`)} className="hover:text-orange-600 hover:underline">{r.item_sku}</button>
                              ) : r.item_sku}
                            </TableCell>
                            <TableCell>{r.item_name || '—'}</TableCell>
                            <TableCell className="text-right mono">{r.negative_events}</TableCell>
                            <TableCell className="text-right mono text-red-600 font-semibold">{r.net_delta}</TableCell>
                            <TableCell className="text-right mono font-semibold text-orange-600">+{r.recommended_reorder_bump}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default InventoryVarianceReport;
